import { createFileRoute } from "@tanstack/react-router";

import { LegalPage, LegalSection } from "@/components/LegalPage";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: "Terms of Service — PortDwellr" },
      {
        name: "description",
        content:
          "The terms governing use of PortDwellr's autonomous yard intelligence platform for drayage yards, cross-docks, and terminals.",
      },
      { property: "og:title", content: "Terms of Service — PortDwellr" },
      {
        property: "og:description",
        content: "The terms governing use of the PortDwellr yard intelligence platform.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: TermsPage,
});

function TermsPage() {
  return (
    <LegalPage title="Terms of Service" updated="Last updated: August 2026">
      <LegalSection heading="Agreement">
        <p>
          These terms govern access to and use of PortDwellr, the autonomous yard intelligence
          platform operated by PortDwellr. By creating an account or using the service you agree to
          them on behalf of your organization.
        </p>
      </LegalSection>
      <LegalSection heading="Accounts and access">
        <p>
          You are responsible for the accuracy of your organization and yard configuration, for
          keeping credentials secure, and for the actions of every member you invite. Access is
          scoped by organization membership and role; you may not attempt to access another
          organization's data.
        </p>
      </LegalSection>
      <LegalSection heading="Acceptable use">
        <p>
          Do not use the service to store unlawful content, interfere with its operation, probe or
          bypass access controls, reverse engineer the platform, or resell access without written
          permission.
        </p>
      </LegalSection>
      <LegalSection heading="Your data">
        <p>
          Your organization retains ownership of the operational data it submits. You grant
          PortDwellr the limited rights needed to host, process, and display that data in order to
          run the service for you, including generating sequencing decisions, assignments, dispute
          timestamp trails, and analytics.
        </p>
      </LegalSection>
      <LegalSection heading="Operational decisions">
        <p>
          PortDwellr produces recommendations and records — sequencing order, dock assignments,
          dwell and detention calculations, and dispute evidence. Final operational and commercial
          decisions remain yours. The service is provided as-is, without warranty that any
          recommendation, timestamp, or detention figure will be accepted by a carrier or third
          party.
        </p>
      </LegalSection>
      <LegalSection heading="Availability and changes">
        <p>
          We aim for continuous availability but may perform maintenance or modify features. We
          will give reasonable notice of material changes to these terms or to functionality your
          organization depends on.
        </p>
      </LegalSection>
      <LegalSection heading="Liability">
        <p>
          To the maximum extent permitted by law, PortDwellr is not liable for indirect,
          incidental, or consequential damages, including lost detention revenue, missed
          appointments, or demurrage. Total liability is limited to the fees paid for the service
          in the twelve months preceding the claim.
        </p>
      </LegalSection>
      <LegalSection heading="Termination">
        <p>
          Either party may terminate at any time. On termination your access ends and data is
          handled as described in the Privacy Policy.
        </p>
      </LegalSection>
      <LegalSection heading="Contact">
        <p>
          Questions about these terms:{" "}
          <a
            className="underline decoration-signal decoration-2 underline-offset-4"
            href="mailto:upton@portdwellr.com"
          >
            upton@portdwellr.com
          </a>
          .
        </p>
      </LegalSection>
    </LegalPage>
  );
}
